import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, formatPrice } from '@/lib/api';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { ShieldCheck, ShoppingCart, Bolt, ArrowLeft } from 'lucide-react';

export default function ProductDetail() {
    const { id } = useParams();
    const [p, setP] = useState(null);
    const [qty, setQty] = useState(1);
    const { add } = useCart();
    const nav = useNavigate();

    useEffect(() => {
        api.get(`/products/${id}`).then(({ data }) => setP(data)).catch(() => setP(false));
    }, [id]);

    if (p === null) return <div className="max-w-7xl mx-auto p-8 text-white/50">Loading…</div>;
    if (p === false) return (
        <div className="max-w-7xl mx-auto p-8">
            <div className="rrm-panel p-10 text-center">
                <div className="text-white/70">Part not found.</div>
                <Link to="/shop" className="inline-block mt-4 text-amber-300">Back to shop</Link>
            </div>
        </div>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Link to="/shop" className="inline-flex items-center text-sm text-white/60 hover:text-white mb-4 gap-1">
                <ArrowLeft className="w-4 h-4" /> All Parts
            </Link>
            <div className="grid lg:grid-cols-2 gap-8">
                <div className="rrm-panel overflow-hidden">
                    <img src={p.image_url} alt={p.name} className="w-full aspect-[4/3] object-cover" />
                </div>
                <div>
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${p.part_type === 'oem' ? 'bg-white/5 border-white/15 text-white/80' : 'bg-rose-500/10 border-rose-500/30 text-rose-200'}`}>
                            {p.part_type === 'oem' ? 'OEM' : 'UPGRADE'}
                        </span>
                        {p.vin_verified && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border bg-emerald-500/10 border-emerald-500/30 text-emerald-200 inline-flex items-center gap-1">
                                <ShieldCheck className="w-3 h-3" /> VIN VERIFIED
                            </span>
                        )}
                    </div>
                    <h1 className="text-3xl md:text-4xl font-display text-white">{p.name}</h1>
                    <div className="mt-2 text-sm text-white/50">{p.brand} · {p.category} · SKU <span className="font-mono text-white/70">{p.sku}</span></div>
                    <div className="mt-5 font-display text-4xl text-amber-400" data-testid="product-price">{formatPrice(p.price)}</div>
                    <div className="mt-2 text-sm text-white/60">{p.stock > 0 ? <span className="text-emerald-300">In stock — ships in 1-2 business days</span> : <span className="text-rose-300">Backordered</span>}</div>
                    <p className="mt-5 text-white/80 leading-relaxed">{p.description}</p>
                    {p.fitment_notes && (
                        <div className="mt-3 text-sm rrm-panel p-3 border-amber-500/20">
                            <div className="text-amber-400 text-xs tracking-widest mb-1">FITMENT NOTE</div>
                            <div className="text-white/80">{p.fitment_notes}</div>
                        </div>
                    )}
                    <div className="mt-6 flex items-center gap-3">
                        <div className="inline-flex items-center bg-white/5 border border-white/10 rounded-md overflow-hidden">
                            <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2 text-white/70 hover:bg-white/5" data-testid="product-qty-minus">−</button>
                            <div className="px-3 text-sm">{qty}</div>
                            <button onClick={() => setQty(Math.min(99, qty + 1))} className="px-3 py-2 text-white/70 hover:bg-white/5" data-testid="product-qty-plus">+</button>
                        </div>
                        <button
                            onClick={() => { add(p, qty); toast.success(`Added ${qty} to cart`); }}
                            data-testid="product-add-to-cart-button"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-white/10 hover:bg-white/15 text-white text-sm"
                        >
                            <ShoppingCart className="w-4 h-4" /> Add to cart
                        </button>
                        <button
                            onClick={() => { add(p, qty); nav('/cart'); }}
                            data-testid="product-buy-now-button"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-[var(--rrm-red)] hover:bg-[var(--rrm-red2)] text-white text-sm font-semibold"
                        >
                            <Bolt className="w-4 h-4" /> Buy now
                        </button>
                    </div>
                    <Link to="/vin" data-testid="product-vin-check-button" className="inline-block mt-4 text-xs text-amber-300 underline">Check fitment with my VIN</Link>
                </div>
            </div>

            {/* Fitment table */}
            {p.compatibility?.length > 0 && (
                <section className="mt-12">
                    <h2 className="font-display text-2xl text-white mb-4">FITMENT</h2>
                    <div className="rrm-panel overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="text-left text-white/50 text-xs uppercase tracking-widest">
                                <tr className="border-b border-white/10">
                                    <th className="p-3">Make</th>
                                    <th className="p-3">Model</th>
                                    <th className="p-3">Years</th>
                                    <th className="p-3">Trims</th>
                                    <th className="p-3">Engines</th>
                                </tr>
                            </thead>
                            <tbody>
                                {p.compatibility.map((c) => (
                                    <tr key={`${c.make}-${c.model}-${(c.years || []).join(',')}`} className="border-b border-white/5">
                                        <td className="p-3 text-white">{c.make}</td>
                                        <td className="p-3 text-white">{c.model}</td>
                                        <td className="p-3 text-white/70 font-mono text-xs">{c.years?.length ? `${c.years[0]}–${c.years[c.years.length - 1]}` : '—'}</td>
                                        <td className="p-3 text-white/70">{c.trims?.join(', ') || 'All'}</td>
                                        <td className="p-3 text-white/70">{c.engines?.join(', ') || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}
        </div>
    );
}
